import api from '@/lib/api';
import { type SeatTypeKey, BillingCycle, SubscriptionStatus } from '@/services/subscriptionService';

export enum SeatStatus {
  ACTIVE = 'ACTIVE',
  RESERVED = 'RESERVED',
  UNASSIGNED = 'UNASSIGNED',
}

export enum SeatFilter {
  ALL = 'ALL',
  UNASSIGNED = 'UNASSIGNED',
  RESERVED = 'RESERVED',
}

/**
 * A single seat an employee holds. Junction model (EmployeeSeat): an employee may
 * hold MANY seats — at most one per seatType (LOCKED rule). Each row is either an
 * ACTIVE seat bound to a SubscriptionItem or a RESERVED seat pending on a SeatPrice.
 */
export interface EmployeeSeatRow {
  seatType: SeatTypeKey;
  seatStatus: SeatStatus;
  subscriptionItemId: string | null;
  subscriptionItem: {
    id: string;
    seatType: SeatTypeKey;
    billingCycle: BillingCycle;
    createdAt: string;
    currentPeriodEnd: string;
    status: SubscriptionStatus;
    cancelAtPeriodEnd: boolean;
  } | null;
  pendingSeatPriceId: string | null;
  pendingSeatPrice: {
    id: string;
    seatType: SeatTypeKey;
    billingCycle: BillingCycle;
  } | null;
}

export interface EmployeeSeatEntry {
  id: string;
  firstName: string;
  lastName: string;
  /**
   * All seats the employee currently holds (multi-seat model). An employee with no
   * package has an empty array. Replaces the former single subscriptionItem/
   * pendingSeatPrice/seatStatus scalar fields.
   */
  seats: EmployeeSeatRow[];
}

export interface SeatListResponse {
  employees: EmployeeSeatEntry[];
  total: number;
  page: number;
  limit: number;
}

export const getSeatList = async (params: {
  filter?: SeatFilter;
  seatType?: string;
  billingCycle?: string;
  page?: number;
  limit?: number;
}): Promise<SeatListResponse> => {
  const response = await api.get('/seat-assignment', { params });
  return response.data.data;
};

export const getSeatCount = async (filter: SeatFilter): Promise<{ total: number; filter: SeatFilter }> => {
  const response = await api.get('/seat-assignment', { params: { filter, limit: 1 } });
  return { total: response.data.data.total, filter };
};


export const assignSeat = async (employeeId: string, seatType: string, billingCycle: string) => {
  const response = await api.post(`/seat-assignment/${employeeId}/assign`, { seatType, billingCycle });
  return response.data.data;
};

/**
 * Assign several packages to one employee atomically. The server applies them all in a
 * single transaction (all-or-nothing), so a failure on any package assigns none — unlike
 * looping assignSeat() on the client, which would leave earlier successes committed.
 */
export const assignSeats = async (
  employeeId: string,
  packages: { seatType: string; billingCycle: string }[]
) => {
  const response = await api.post(`/seat-assignment/${employeeId}/assign-multi`, { packages });
  return response.data.data;
};

/**
 * Targets ONE of the employee's seats. In the multi-seat model an employee may hold
 * several seats, so `seatType` disambiguates which one to act on. The server also
 * accepts an omitted seatType when the employee has exactly one seat.
 */
export const revokeSeat = async (employeeId: string, seatType?: string) => {
  const response = await api.delete(`/seat-assignment/${employeeId}/revoke`, {
    params: seatType ? { seatType } : undefined,
  });
  return response.data.data;
};

/**
 * Detaches ONE seat from the employee WITHOUT cancelling the subscription. The
 * SubscriptionItem stays alive and the freed slot becomes assignable to someone
 * else. `seatType` selects which of the employee's seats to release.
 */
export const releaseSeat = async (employeeId: string, seatType?: string) => {
  const response = await api.delete(`/seat-assignment/${employeeId}/release`, {
    params: seatType ? { seatType } : undefined,
  });
  return response.data.data;
};

export const reactivateSeat = async (employeeId: string, seatType?: string) => {
  const response = await api.patch(`/seat-assignment/${employeeId}/reactivate`, undefined, {
    params: seatType ? { seatType } : undefined,
  });
  return response.data.data;
};

export const transferSeat = async (
  fromEmployeeId: string,
  toEmployeeId: string,
  seatType?: string,
) => {
  const response = await api.post('/seat-assignment/transfer', {
    fromEmployeeId,
    toEmployeeId,
    ...(seatType ? { seatType } : {}),
  });
  return response.data.data;
};
